import React, { forwardRef } from "react";
import styled from "styled-components/macro";
import { useLocation, useNavigate } from "react-router-dom";
import { rgba, darken } from "polished";

import {
  Collapse,
  Link,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import swal from "sweetalert";

const CustomRouterLink = forwardRef((props, ref) => (
  <div ref={ref}>
    <Link {...props} sx={props.sx} />
  </div>
));

const Item = styled(ListItemButton)`
  padding-top: ${(props) =>
    props.theme.spacing(props.depth && props.depth > 0 ? 2 : 3)};
  padding-bottom: ${(props) =>
    props.theme.spacing(props.depth && props.depth > 0 ? 2 : 3)};
  padding-left: ${(props) =>
    props.theme.spacing(props.depth && props.depth > 0 ? 14 : 8)};
  padding-right: ${(props) =>
    props.theme.spacing(props.depth && props.depth > 0 ? 4 : 7)};
  font-weight: ${(props) => props.theme.typography.fontWeightRegular};
  svg {
    color: ${(props) => props.theme.sidebar.color};
    font-size: 20px;
    width: 20px;
    height: 20px;
    opacity: 0.5;
  }
  &:hover {
    background: rgba(0, 0, 0, 0.08);
    color: ${(props) => props.theme.sidebar.color};
  }
  &.${(props) => props.activeclassname} {
    background-color: ${(props) =>
      darken(0.03, props.theme.sidebar.background)};
    span {
      color: ${(props) => props.theme.sidebar.color};
    }
  }
`;

const Title = styled(ListItemText)`
  margin: 0;
  span {
    color: ${(props) =>
      rgba(
        props.theme.sidebar.color,
        props.depth && props.depth > 0 ? 0.7 : 1
      )};
    font-size: ${(props) => props.theme.typography.body1.fontSize}px;
    padding: 0 ${(props) => props.theme.spacing(4)};
  }
`;

const ItemText = styled(ListItemText)`
  margin: 0;
  span {
    color: ${(props) =>
      rgba(
        props.theme.sidebar.color,
        props.depth && props.depth > 0 ? 0.7 : 1
      )};
    font-size: ${(props) => props.theme.typography.body1.fontSize}px;
    padding: 0 ${(props) => props.theme.spacing(4)};
  }
`;

const ExpandLessIcon = styled(ExpandLess)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`;

const ExpandMoreIcon = styled(ExpandMore)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`;

const SidebarNavListItem = (props) => {
  const {
    title,
    href,
    depth = 0,
    children,
    preventNavigation,
    setPreventNavigation,
    icon: Icon,
    open: openProp = false,
  } = props;
  const location = useLocation();

  const [open, setOpen] = React.useState(openProp);

  const handleToggle = () => {
    setOpen((state) => !state);
  };

  let path = location.pathname;
  const navigate = useNavigate();

  const onNavigate = (path) => {
    if (preventNavigation) {
      return changesLost(() => navigate(path));
    }
    navigate(path);
  };

  const changesLost = (callback) => {
    swal({
      title: "Are you sure?",
      text: "Changes that you made may not be saved.",
      icon: "warning",
      buttons: ["Leave", "Stay"],
    }).then((res) => {
      if (res) {
        // do nothing
      } else {
        callback();
        setPreventNavigation(false);
      }
    });
  };

  if (children) {
    return (
      <React.Fragment>
        <Item
          onClick={() => {
            handleToggle();
            onNavigate(href);
          }}
          component={CustomRouterLink}
          to={href}
          sx={
            path.includes(href)
              ? { backgroundColor: "#1e293a" }
              : { backgroundColor: "none" }
          }
        >
          <Icon />
          <ItemText primary={title} />
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Item>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {children &&
              children.map((item) => (
                <Item
                  depth={1}
                  key={item.title}
                  component={CustomRouterLink}
                  onClick={() => {
                    onNavigate(item.href);
                  }}
                  to={item.href}
                  // activeclassname="active"
                  sx={
                    path === item.href
                      ? { backgroundColor: "#1e293a" }
                      : { backgroundColor: "none" }
                  }
                >
                  <ItemText primary={item.title} />
                </Item>
              ))}
          </List>
        </Collapse>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Item
        depth={depth}
        component={CustomRouterLink}
        to={href}
        onClick={() => {
          onNavigate(href);
        }}
        sx={
          path.includes(href)
            ? { backgroundColor: "#1e293a" }
            : { backgroundColor: "none" }
        }
      >
        {Icon && <Icon />}
        <Title depth={depth}>{title}</Title>
      </Item>
    </React.Fragment>
  );
};

export default SidebarNavListItem;
